import defaultAttributes from "backend/defaultAttributes";
import useComments from "backend/hooks/comments/useComments";
import useElement from "backend/hooks/elements/useElement";
import useAttachments from "backend/hooks/files/useAttachments";
import useYearsLastPeriod from "backend/hooks/model/useYearsLastPeriod";
import { ICardElementWithChildren } from "backend/model/model";
import getValues from "backend/values";
import PerformanceTrend from "components/PerformanceTrend";
import SubElements from "components/subelements/SubElements";
import KPIToolbar from "components/toolbars/KPIToolbar";
import ValueHistory from "components/ValueHistory";
import AnalysisCard from "design/components/cards/AnalysisCard/AnalysisCard";
import {
	KPIPerformanceCard,
	YearEndTargetCard,
} from "design/components/cards/KPIPerformanceCard";
import MeasureInfo from "design/components/cards/MeasureInfo";
import ItemDrawer from "design/components/menus/ItemDrawer";
import InitiativeHeading from "design/components/texts/InitiativeHeading";
import LeftMenuPosition from "design/layouts/LeftMenuPosition";
import SplitView from "design/layouts/SplitView";
import StackView from "design/layouts/StackView";
import Page from "design/pages/Page";
import { t } from "i18next";
import { resultSort } from "init/sort";
import { getTranslatedAttribute } from "qpr-base//tplutils/utils";
import * as conf from "qpr-base/configuration";
import { useAppContext } from "qpr-base/context/AppCtxProvider";
import useLink from "qpr-base/hooks/useLink";
import useQueryBuilder from "qpr-base/querybuilder/useQueryBuilder";
import Anchor from "qpr-base/ui/Anchor";
import Loader from "qpr-base/ui/components/Loader";
import UploadedFilesList from "qpr-base/ui/components/UploadedFilesList";
import PageContentMenu from "qpr-base/uicomponents/view/PageContentMenu";
import { isValidId } from "qpr-base/utils/url-utils";
import React, { useMemo,useState,useEffect } from "react";
import { useHistory } from "react-router-dom";
import { Button } from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";

import { useQuery } from "react-query";
import useCreateAction from "backend/hooks/actions/useCreateAction";
import useAllActions, { type ActionData } from "backend/hooks/actions/useAllActions";
import ActionDialog from "components/forms/valueinput/ActionDialog";
import ActionsPreview from "design/components/cards/ActionsPreview";
// already added

const KPIDetails = () => {
	const ctx = useAppContext();
	const link = useLink();
	const history = useHistory();

	const { data: comments } = useComments({
		id: ctx.elementId || "",
		period: ctx.period || "",
	});
	const { data: attachments } = useAttachments({
		elementId: ctx.elementId || "",
		period: ctx.period,
	});
	const { data: kpi, isLoading } = useElement(ctx.elementId);
	const { data: kpiParent } = useElement(kpi ? kpi.parent_element_id : "");
	const { data: grandParent } = useElement(
		kpiParent?.parent_element_id ? kpiParent.parent_element_id : undefined
	);
	const getParentDetails = React.useCallback(
		(element: ICardElementWithChildren | undefined) =>
			(getTranslatedAttribute(element, "Name", ctx.language) || "") +
			"\n" +
			getTranslatedAttribute(element, "Description", ctx.language),
		[ctx.language]
	);

	const { data: scorecardElements, isLoading: isScorecardElementsLoading } =
		useQueryBuilder<ICardElementWithChildren[]>((q) =>
			q(isValidId(ctx.elementId || "") ? ctx.elementId : "")
				.scorecard()
				.attributes(...defaultAttributes(ctx.period, []))
				.measures()
		);

	const [sortedScorecardKPIs, subElements] = React.useMemo(() => {
		return [
			resultSort(
				scorecardElements?.data.filter(
					(element) => element.typename === "KPI"
				) || []
			),
			scorecardElements?.data.filter(
				(element) => element.parent_element_id === ctx.elementId
			),
		];
	}, [scorecardElements]);

	const lastPeriod = useYearsLastPeriod(ctx.period || "");

	const { data: lastPeriodValue } = useQuery(
		`last-period-for-${ctx.elementId}-${ctx.period}-${lastPeriod?.name}`,
		async () => {
			return getValues(isValidId(ctx.elementId || "") ? (ctx.elementId || "") : "", [lastPeriod!], true, []);
		},
		{ enabled: !!lastPeriod }
	);

  const [firstComment] = comments || [];
  const [isActionDialogOpen, setIsActionDialogOpen] = useState(false);

  const { data: allActions } = useAllActions();
  const actions: ActionData[] | undefined = useMemo(
    () =>
      allActions?.filter(
        (action) => action.searchKpiInitiativeId === (ctx.elementId || "")
      ),
    [allActions, ctx.elementId]
  );
	const createActionMutation = useCreateAction();

	const kpiInfo = useMemo(() => ({
		id: ctx.elementId || "",
		typename: "KPI" as const,
		name: getTranslatedAttribute(kpi, "Name", ctx.language) || "",
	}), [ctx.elementId, kpi, ctx.language]);

	useEffect(() => {
		if (createActionMutation.isSuccess) {
			setIsActionDialogOpen(false);
		}
	}, [createActionMutation.isSuccess]);

	if (!kpi || !kpiParent) {
		return <Loader />;
	}

	return (
		<Page
			isLoading={
				isLoading ||
				isScorecardElementsLoading ||
				!scorecardElements?.data ||
				!kpi
			}
			Toolbar={KPIToolbar}
		>
			<SplitView left="180px" right="1100px" gap="20px">
				<div>
					<LeftMenuPosition>
						<ItemDrawer
							rtl={ctx.rtl}
							title={t("kpis.KPIs")}
							items={sortedScorecardKPIs || []}
							onSelect={(item) => {
								link({ meaId: item.id });
							}}
						/>
						<PageContentMenu
							componentData={{
								items: [
									{ title: t("kpis.KPI Information"), anchor: "header" },
									{ title: t("kpis.Performance"), anchor: "performance" },
									{ title: t("kpis.Justification"), anchor: "justification" },
									{ title: t("kpis.Value data"), anchor: "valuedata" },
									{ title: t("kpis.Sub Elements"), anchor: "subelements" },
								],
							}}
						/>
					</LeftMenuPosition>
				</div>
				<div>
					<StackView>
						<Anchor name="header" />
						<MeasureInfo
							title={t("kpis.KPI Information")}
							items={[
								{
									label: t("kpis.Description"),
									text:
										getTranslatedAttribute(kpi, "Description", ctx.language) ||
										"",
								},
								{
									label: t("kpis.Measurement Unit"),
									text: t("configtypes."+kpi!.ACT_unitname) || "",
								},
								{
									label: t("kpis.Strategic Objective"),
									text:
										(kpi?.parentTypename || "") === "Strategic Objective"
											? getParentDetails(kpiParent)
											: (grandParent?.typename || "") ===
											"Strategic Objective"
												? getParentDetails(grandParent)
												: "",
								},
								{
									label: t("kpis.Measurement Frequency"),
									text: kpi!.periodlevelname
										? t("periodlevels." + kpi!.periodlevelname)
										: "",
								},
							]}
						/>

						<SplitView left="30%" right="70%" gap="30px">
							<div>
								<InitiativeHeading>{t("kpis.Performance")}</InitiativeHeading>

								<KPIPerformanceCard
									period={kpi.periodname || ""}
									element={kpi}
									settings={conf.getKPIDetailsViewGaugeSettings(kpi)}
								/>

								{lastPeriodValue && lastPeriodValue[0] ? (
									<YearEndTargetCard
										element={lastPeriodValue[0]}
										settings={conf.getKPIDetailsViewTargetCardSettings(
											lastPeriodValue[0]
										)}
									/>
								) : null}
							</div>
							<div>
								<Anchor name="performance" />
								<InitiativeHeading>
									{t("kpis.Performance Trend")}
								</InitiativeHeading>
								<PerformanceTrend
									element={kpi!}
									settings={conf.getKPIDetailsViewChartSettings(kpi)}
								/>
							</div>
						</SplitView>

						<Anchor name="justification" />
						<InitiativeHeading subTitle={ctx.period}>
							{/* {t("kpis.Actions and Recommendations")} */}
							{t("kpis.Justifications & Recommendations")}
						</InitiativeHeading>

						{firstComment && kpi.STATUS === "1" ? (
							<AnalysisCard
								userData={{
									name: firstComment.modifiedby || firstComment.createdby,
									subtitle: t("kpis.Owner"),
								}}
								date={new Date(
									firstComment.lastmodifieddate
								).toLocaleDateString()}
								description={
									firstComment
										? firstComment.description
										: t("kpis.No Comments yet")
								}
							>
								<UploadedFilesList values={attachments} readOnly />
							</AnalysisCard>
						) : (
							<AnalysisCard description={t("kpis.No Comments Yet")}>
								{kpi.STATUS === "1" ? (
									<UploadedFilesList values={attachments} readOnly />
								) : null}
							</AnalysisCard>
						)}

						{/* New Actions Section */}
						<InitiativeHeading>{t("Actions.Actions")}</InitiativeHeading>
            <div style={{ marginTop: "16px", marginBottom: "24px" }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => setIsActionDialogOpen(true)}
                style={{ marginBottom: "16px" }}
              >
                {t("Actions.Add New Action")}
              </Button>
              <ActionsPreview
                actions={actions || []}
                kpiInitiativeName={kpiInfo.name}
              />
            </div>
						<ActionDialog
							open={isActionDialogOpen}
							onClose={() => setIsActionDialogOpen(false)}
							kpiOrInitiative={kpiInfo}
						/>

						<Anchor name="valuedata" />
						<InitiativeHeading>{t("kpis.Value data")}</InitiativeHeading>
						<ValueHistory element={kpi!} />

						<Anchor name="subelements" />
						<InitiativeHeading>{t("kpis.Sub Elements")}</InitiativeHeading>
						{subElements?.length ? (
							<div style={{ width: "600px" }}>
								<SubElements
									isDisabled={true}
									handleCalculator={async () => {}}
									elements={subElements || []}
									updateResult={(val: any) => {}}
									key={kpi?.id}
								/>
								<div style={{ height: "600px" }}></div>
							</div>
						) : (
							<>
								---
								<div style={{ height: "600px" }}></div>
							</>
						)}
					</StackView>
				</div>
			</SplitView>
		</Page>
	);
};

export default KPIDetails;
